import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import { BASE_URL } from "../api";
import { getApiHeaders } from "../utils/helpers";

// ─────────────────────────────────────────────────────────────
// OLD Deposit page is commented out below. New crypto deposit
// functionality (same as Apex project) is implemented above.
// ─────────────────────────────────────────────────────────────

export default function Deposit() {
  const navigate = useNavigate();
  const [coinList, setCoinList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeposit, setLoadingDeposit] = useState(false);
  const [apiError, setApiError] = useState("");

  const [amount, setAmount] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState("");

  useEffect(() => {
    fetchCoinList();
  }, []);

  const fetchCoinList = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/account/coin_list`, {
        headers: getApiHeaders(),
      });
      if (res.data && res.data.data) {
        const coins = res.data.data.filter(
          (coin) => coin.is_deposit_enabled && coin.is_active
        );
        setCoinList(coins);
        if (coins.length > 0) {
          const firstNetwork = coins[0].network_name;
          setSelectedNetwork(firstNetwork);
          const symbols = coins.filter((c) => c.network_name === firstNetwork);
          if (symbols.length > 0) {
            setSelectedSymbol(symbols[0].coin_symbol);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching coin list", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNetworkChange = (net) => {
    setSelectedNetwork(net);
    const symbols = coinList.filter((c) => c.network_name === net);
    if (symbols.length > 0) {
      setSelectedSymbol(symbols[0].coin_symbol);
    } else {
      setSelectedSymbol("");
    }
  };

  const uniqueNetworks = [...new Set(coinList.map((c) => c.network_name))];
  const availableSymbols = coinList.filter(
    (c) => c.network_name === selectedNetwork
  );
  const selectedCoinData = coinList.find(
    (c) =>
      c.network_name === selectedNetwork && c.coin_symbol === selectedSymbol
  );

  const numVal = parseFloat(amount);
  const isValidAmount =
    selectedCoinData &&
    numVal >= selectedCoinData.min_deposit &&
    numVal <= selectedCoinData.max_deposit;

  const handleDeposit = async () => {
    if (!isValidAmount || !selectedCoinData) return;

    setLoadingDeposit(true);
    setApiError("");
    try {
      const payload = {
        amount: String(amount),
        coin: String(selectedCoinData.coin_symbol),
        coin_type: String(selectedCoinData.network_name),
      };

      const res = await axios.post(`${BASE_URL}/before_deposit`, payload, {
        headers: getApiHeaders(),
      });

      if (res.data) {
        if (res.data.status === "error" || res.data.code === 400) {
          setApiError(res.data.msg || "Invalid request parameters.");
          return;
        }
        navigate(
          `/crypto_deposit?coin=${selectedCoinData.network_name} ${selectedCoinData.coin_symbol}&amount=${amount}&address=${res.data.address || ""}&orderno=${res.data.orderno || ""}&invoiceid=${res.data.invoiceid || ""}`
        );
      }
    } catch (error) {
      console.error("Error calling before_deposit API", error);
      const errMsg =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        "Something went wrong. Please try again.";
      setApiError(errMsg);
    } finally {
      setLoadingDeposit(false);
    }
  };

  const getCoinImage = (symbol, network) => {
    const sym = String(symbol || "").toUpperCase();
    const net = String(network || "").toUpperCase();
    if (sym === "USDT" && net === "BEP20")
      return "assets/images/deposit/Tether_BEP20_USDT.svg";
    if (sym === "USDT" && net === "TRC20")
      return "assets/images/deposit/Tether_TRC20_USDT.svg";
    return "assets/images/deposit/wallet.svg";
  };

  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="fw-700 mb-0">Deposit</h2>
                    </div>
                    <div className="page-title-right">
                      <button
                        className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                        onClick={() => navigate("/crypto_deposit_history")}
                      >
                        <i className="bx bx-history me-2 fs-5"></i> Deposit History
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row justify-content-center mt-1">
                <div className="col-xl-5 col-lg-6 col-md-8">
                  <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-body p-3 p-sm-4">
                      {loading ? (
                        <div className="d-flex justify-content-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : coinList.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          No payment methods available.
                        </div>
                      ) : (
                        <div>
                          <h4
                            className="fw-bold mb-3 pb-3 border-bottom pb-2"
                          >
                            Crypto Deposit
                          </h4>

                          {/* Network Selection */}
                          <div className="mb-4">
                            <label className="form-label text-muted fw-semibold">
                              Select Network
                            </label>
                            <div className="row g-3">
                              {uniqueNetworks.map((netName) => {
                                const isSelected = selectedNetwork === netName;
                                const repCoin = coinList.find(
                                  (c) => c.network_name === netName
                                );
                                return (
                                  <div className="col-sm-6" key={netName}>
                                    <div
                                      onClick={() => handleNetworkChange(netName)}
                                      className={`rounded-3 p-3 px-3 d-flex align-items-center notranslate ${isSelected ? 'bg-primary bg-opacity-10 border border-primary shadow-sm' : 'bg-transparent border'}`}
                                      translate="no"
                                      style={{
                                        cursor: "pointer",
                                        transition: "all 0.2s ease"
                                      }}
                                    >
                                      <img
                                        src={getCoinImage(repCoin?.coin_symbol, netName)}
                                        alt={netName}
                                        className="me-3 notranslate"
                                        translate="no"
                                        width="32"
                                        height="32"
                                      />
                                      <div>
                                        <div className={`fw-bold notranslate ${isSelected ? 'text-primary' : 'text-body'}`} translate="no">
                                          {netName}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Coin Selection */}
                          {selectedNetwork && (
                            <div className="mb-4">
                              <label className="form-label text-muted fw-semibold">
                                Choose Coin
                              </label>
                              <div className="d-flex flex-wrap gap-3">
                                {availableSymbols.map((coin) => {
                                  const isCoinSelected =
                                    selectedSymbol === coin.coin_symbol;
                                  return (
                                    <div
                                      key={coin.id}
                                      onClick={() => setSelectedSymbol(coin.coin_symbol)}
                                      className={`rounded-3 p-2 px-3 d-flex flex-column align-items-center justify-content-center text-center notranslate ${isCoinSelected ? 'bg-primary bg-opacity-10 border border-primary shadow-sm' : 'bg-transparent border'}`}
                                      translate="no"
                                      style={{
                                        minWidth: "125px",
                                        cursor: "pointer",
                                        transition: "all 0.2s ease"
                                      }}
                                    >
                                      <div
                                        className={`fw-bold notranslate ${isCoinSelected ? 'text-primary' : 'text-body'}`}
                                        translate="no"
                                        style={{ fontSize: "1rem", lineHeight: "1.2" }}
                                      >
                                        {coin.coin_symbol}
                                      </div>
                                      <span
                                        className={`mt-1 notranslate ${isCoinSelected ? 'text-primary text-opacity-75' : 'text-secondary'}`}
                                        translate="no"
                                        style={{ fontSize: "0.78rem", fontWeight: "500", lineHeight: "1.2" }}
                                      >
                                        {coin.coin_name || (coin.coin_symbol === "USDT" ? "Tether USD" : coin.coin_symbol)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Amount input */}
                          {selectedCoinData && (
                            <div className="mb-4">
                              <label className="form-label text-muted fw-semibold">
                                Amount
                              </label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control px-3 py-2"
                                  placeholder={`Min Deposit: $${selectedCoinData.min_deposit}`}
                                  value={amount}
                                  style={{ borderRadius: "8px 0 0 8px" }}
                                  onKeyDown={(e) => {
                                    if (["e", "E", "+", "-"].includes(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                      setAmount("");
                                      setApiError("");
                                      return;
                                    }
                                    if (
                                      val.includes("-") ||
                                      val.toLowerCase().includes("e")
                                    ) {
                                      return;
                                    }
                                    const num = parseFloat(val);
                                    if (isNaN(num) || num < 0) return;
                                    if (
                                      selectedCoinData &&
                                      num > selectedCoinData.max_deposit
                                    ) {
                                      return;
                                    }
                                    setAmount(val);
                                    setApiError("");
                                  }}
                                />
                                <span className="input-group-text fw-bold notranslate" translate="no" style={{ borderRadius: "0 8px 8px 0" }}>USD</span>
                              </div>
                              <small className="text-muted mt-1 d-block">
                                Min Deposit: ${selectedCoinData.min_deposit} &nbsp;|&nbsp; Max Deposit: ${selectedCoinData.max_deposit}
                              </small>
                            </div>
                          )}

                          {/* API error */}
                          {apiError && (
                            <div className="alert alert-danger py-2 rounded-3 d-flex align-items-center gap-2 mb-4">
                              <i className="bx bx-info-circle fs-5"></i>
                              <span>{apiError}</span>
                            </div>
                          )}

                          {/* Info notice */}
                          <div className="mb-4">
                            <small className="text-muted mt-1 d-block">
                              <i className="bx bx-info-circle me-1"></i>
                              Please ensure you send only <strong className="notranslate" translate="no">USDT</strong> to your deposit address. Assets sent to the wrong network cannot be recovered.
                            </small>
                          </div>

                          {/* Submit */}
                          <div className="text-end mt-4">
                            <button
                              className="btn btn-primary px-4 py-2 fw-medium"
                              onClick={handleDeposit}
                              disabled={!isValidAmount || loadingDeposit}
                            >
                              {loadingDeposit ? (
                                <span>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Processing...
                                </span>
                              ) : (
                                <span>
                                  Submit Deposit
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* container-fluid */}
          </div>
          {/* End Page-content */}
          <Footer />
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   OLD DEPOSIT PAGE (commented out)
   ─────────────────────────────────────────────────────────────

import React from "react";
import { Link } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import CommonTable from "../component/CommonTable";

export default function Deposit() {
  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="fw-700 mb-sm-0">Deposit</h2>
                      <p className="text-muted mb-0 mt-2">Select your preferred payment method.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="card-body">
                            <div className="table-responsive">
                              <CommonTable
                                columns={[
                                  { header: "Payment Method", ... },
                                  { header: "Currency", ... },
                                  { header: "Fees/Commission(%)", ... },
                                  { header: "Processing Time", ... },
                                  { header: "Action", ... },
                                ]}
                                data={[
                                  { image: "assets/images/deposit/Tether_BEP20_USDT.svg", title: "Tether (BEP20 USDT)", currency: "USD", commission: "0 %", processingTime: "1 Hour", link: "/deposit_tether?coin=BEP20 USDT" },
                                  { image: "assets/images/deposit/Tether_TRC20_USDT.svg", title: "Tether (TRC20 USDT)", currency: "USD", commission: "0 %", processingTime: "1 Hour", link: "/deposit_tether?coin=TRC20 USDT" },
                                  { image: "assets/images/deposit/wallet.svg", title: "Wallet", currency: "USD", commission: "0 %", processingTime: "24 Hours", link: "/deposit_wallet", disabled: true },
                                ]}
                                tableClassName="table table-nowrap align-middle table-nowrap mb-0"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}
*/
